import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Constants } from '../../models/constants.class';
import { EntityService } from '../../services/entity.service';
import { Psd2Service } from '../../services/psd2.service';
import {
  AggregatioRequestInterface,
  AggregationResponseInterface,
} from '../../models/aggregation.interface';
import { popupWindow } from '../../helpers/utils';
import { Location } from '@angular/common';
import { TealiumService } from '@app/services/tealium.service';
import { TranslateService } from '@ngx-translate/core';
import { ConfigService } from '@ng-darwin/config';

/**
 * @description Component
 * @class AgTermsComponent
 * @implements {OnInit} OnInit
 * @implements {OnDestroy} OnDestroy
 */
@Component({
  standalone: false,
  selector: 'app-ag-terms',
  templateUrl: './ag-terms.component.html',
  styleUrls: ['./ag-terms.component.scss'],
})
export class AgTermsComponent implements OnInit, OnDestroy {
  public step = 1;
  public constants = Constants;
  // Configuración de ventanas para PSD
  public emergentWindow: any; // Ventana emergente
  public windowState = 0; // Estado de la ventana

  public canGoBack = true;
  public paramsString: any = '';
  public params: any[] = [];
  public isWebview = false;
  public isPopupsModalOpened = false;
  /**
   * @description Manejador para el evento message
   * @param event MessageEvent
   */
  public readonly windowEventListener: (event: MessageEvent) => Promise<void> =
    async (event: MessageEvent) => {
      const code = event.data.code || '';
      if (
        event.data.event_id === Constants.windowEvents.loginId &&
        code !== ''
      ) {
        this._psd2Service.setConsentCode(code);
        console.log('navegación a messages windowEventListener');
        this.router.navigate([Constants.url.messages]);
      }
    };

  /**
   * @description Creates an instance of AgTermsComponent.
   * @param router Router
   * @param entityService EntityService
   * @param _psd2Service Psd2Service
   * @param _location Location
   * @param _tealiumService TealiumService
   */
  constructor(
    public readonly router: Router,
    public readonly entityService: EntityService,
    private readonly _psd2Service: Psd2Service,
    private readonly _location: Location,
    private readonly _tealiumService: TealiumService,
    private readonly _translateService: TranslateService,
    private readonly _configService: ConfigService,
  ) {
    const isWebview = sessionStorage.getItem('iswebview');
    this.isWebview = Boolean(isWebview && isWebview === 'true');
  }

  /**
   * @description ngOnInit
   */
  ngOnInit(): void {
    console.log('Entity selected:', this.entityService.getSelectedEntity());
    if (Object.keys(this.entityService.getSelectedEntity()).length === 0) {
      Promise.resolve(this.router.navigate([Constants.url.index]));
    }
    this.windowEvent();
    this.paramsString = sessionStorage.getItem(Constants.url.params);
    this.params = JSON.parse(this.paramsString);
    if (
      this.step === 1 &&
      this.getSelectedEntityType() === Constants.entityPSD2Type.a1
    ) {
      this._tealiumService.sendPageLoad({
        page_name: '/PSD2/agregar_banco/permitir_conexion/A1',
        page_title: 'PSD2 agregar banco permitir conexion A1',
        page_type: 'detalle',
        page_category: 'servicing',
        product_scope: 'operativa_no_financiera',
        product_category: 'PSD2',
        product_name: 'crédito digital',
        added_bank: this.getSelectedEntityName(),
      });
    } else {
      this._tealiumService.sendPageLoad({
        page_name:
          '/PSD2/agregar_banco/anadir_cuenta' +
          `/${this.getSelectedEntityType()}`,
        page_title:
          'PSD2 agregar banco añadir cuenta ' + this.getSelectedEntityType(),
        page_type: 'detalle',
        page_category: 'servicing',
        product_scope: 'operativa_no_financiera',
        product_category: 'PSD2',
        product_name: 'crédito digital',
        added_bank: this.getSelectedEntityName(),
      });
    }
  }

  /**
   * @description ngOnDestroy
   */
  ngOnDestroy(): void {
    window.removeEventListener('message', this.windowEventListener);
  }

  /**
   * @description incrementStep
   */
  incrementStep(): void {
    this.step++;
    if (
      (this.step === 2 &&
        this.getSelectedEntityType() === Constants.entityPSD2Type.a2) ||
      (this.step === 3 &&
        this.getSelectedEntityType() === Constants.entityPSD2Type.a1)
    ) {
      this.aggregationPsd2();
    }
  }

  /**
   * @description getSelectedEntityType
   * @returns (string | undefined)
   */
  getSelectedEntityType(): string | undefined {
    return this.entityService.getSelectedEntity().type;
  }

  /**
   * @description getSelectedEntityName
   * @returns (string | undefined)
   */
  getSelectedEntityName(): string | undefined {
    return this.entityService.getSelectedEntity().name;
  }

  /**
   * @description getSelectedEntityImage
   * @returns (string | undefined)
   */
  getSelectedEntityImage(): string | undefined {
    return this.entityService.getSelectedEntity().image;
  }

  /**
   * @description manageErrorResponse
   * @param errorResponse any
   * @param aggregationResponse AggregationResponseInterface
   */
  manageErrorResponse(
    errorResponse: any,
    aggregationResponse: AggregationResponseInterface,
  ): void {
    aggregationResponse = errorResponse.error;
    switch (errorResponse.status) {
      case 401:
        this.handle401Error(aggregationResponse);
        break;
      case 403:
        if (aggregationResponse.consentURL) {
          this._psd2Service.setConsentCode('consentNoOk');
          console.log('navegación a messages ag-terms');
          Promise.resolve(this.router.navigate([Constants.url.messages]));
        } else {
          this._tealiumService.sendError({
            category: 'Consent error',
            action: 'warning',
            data: {
              cod_error: 403,
              desc_error: 'popup window',
            },
          });
          this.step = -1;
        }
        break;
      default:
        this._tealiumService.sendError({
          category: 'Server error',
          action: 'error',
          data: {
            cod_error: 500,
            desc_error: 'popup window',
          },
        });
        this.step = -1;
        break;
    }
  }

  private handle401Error(
    aggregationResponse: AggregationResponseInterface,
  ): void {
    if (aggregationResponse.authorizationURL) {
      if (this.isWebview) {
        sessionStorage.setItem('iswebview', 'true');
      }
      this.emergentWindow = popupWindow(
        aggregationResponse.authorizationURL || '',
        true,
      );
      try {
        this.emergentWindow.focus();
        this.windowState = 1;
        this.windowCheckInterval();
      } catch (e) {
        if (!this.isWebview) {
          this.windowState = 2;
          this._tealiumService.sendError({
            category: 'Popup windows are blocked',
            action: 'warning',
            data: {
              cod_error: 401,
              desc_error: 'popup window',
            },
          });
          this.isPopupsModalOpened = true;
        }
      }
    } else {
      this._tealiumService.sendError({
        category: 'Server error',
        action: 'warning',
        data: {
          cod_error: 401,
          desc_error: 'popup window',
        },
      });
      this.step = -1;
    }
  }

  /**
   * @description aggregationPsd2
   */
  aggregationPsd2(): void {
    const aggregationRequest: AggregatioRequestInterface = {};
    let aggregationResponse: AggregationResponseInterface = {};
    const product = this.entityService.getSelectedEntity().products || [];
    aggregationRequest.aspspcode = this.entityService.getSelectedEntity().code;
    aggregationRequest.products = product
      .filter((value) => value.type === Constants.entityType.psd2)
      .map((value) => value.name || '');
    aggregationRequest.params = JSON.parse(
      sessionStorage.getItem(Constants.url.params) as string,
    );
    aggregationRequest.params?.push({
      name: Constants.language,
      value: this._translateService.getDefaultLang(),
    });
    if (
      sessionStorage.getItem('app-unique-id') ===
        this._configService.config.app['cdncApplication'] &&
      this.isWebview
    ) {
      aggregationRequest.params?.push({
        name: 'token',
        value: sessionStorage.getItem('_dwtk') as string,
      });
    }
    // Recuperar el expediente de la sessionStorage y añadirlo al aggregationRequest.expedientCode
    aggregationRequest.expedientCode =
      sessionStorage.getItem('prop-unique-id') || '';
    this._psd2Service
      .postAggregation(aggregationRequest)
      .then((response) => {
        aggregationResponse = response || {};
        // se debe mostrar el mensaje que sea definido
        this._psd2Service.setConsentCode(Constants.consentOk);
        console.log('navegación a messages aggregationPsd2');
        return this.router.navigate([Constants.url.messages]);
      })
      .catch((errorResponse) => {
        this.manageErrorResponse(errorResponse, aggregationResponse);
      });
  }

  /**
   * @description windowEvent
   */
  windowEvent(): void {
    window.addEventListener('message', this.windowEventListener);
  }

  /**
   * @description windowCheckInterval
   */
  windowCheckInterval(): void {
    setInterval(() => {
      this.checkWin();
    }, 1000);
  }

  /**
   * @description checkWin
   */
  checkWin(): void {
    if (!this.emergentWindow) {
      this.windowState = 0; // Estado ventana 0
    } else {
      if (this.emergentWindow.closed) {
        this.windowState = 2; // Estado ventana 2, cerrada
      } else {
        this.windowState = 1; // Estado ventana 1, abierta
      }
    }
  }

  /**
   * @description isPopupWindowClosed
   * @returns boolean
   */
  isPopupWindowClosed(): boolean {
    return this.windowState === 2;
  }

  /**
   * @description exit
   * @returns Promise<boolean>
   */
  exit(): Promise<boolean> {
    return this.router.navigate([Constants.url.index]);
  }

  /**
   * @description back
   */
  back(): void {
    this._location.back();
  }
}
